import Time from "common/Time";
import SummaryModule from "views/wizard/components/summaryModule/SummaryModule";
import { ExternalUser, Invitation } from "models/customers/customers";
import { useGlobalContext } from "app/store/GlobalContext";
import { isValidDate } from "libs/date-util";

import "./InviteExternalUsers.scss";

type Props = {
  externalUsers: ExternalUser[];
  onInviteUser: (email: string, invite: boolean) => void;
  onInviteAllUsers: (invite: boolean) => void;
  isWizard: boolean;
  onEditClick?: () => void;
};

export default function InviteExternalUsers({
  externalUsers,
  onInviteUser,
  onInviteAllUsers,
  isWizard
}: Props) {
  const { setEditingState } = useGlobalContext();

  const allChecked = (newUsers: ExternalUser[]) => {
    for (const user of newUsers) {
      if (!user.sendInvite) {
        return false;
      }
    }
    return true;
  };

  const createInviteText = (id: string, invitation: Invitation) => {
    if (!id) {
      return "";
    }

    if (!invitation) {
      return "(användaren är ännu inte inbjuden)";
    }

    if (isValidDate(invitation.lastInvitaionSentAt)) {
      return (
        "(en inbjudan skickades senast " +
        Time.formatLong(invitation.lastInvitaionSentAt) +
        ")"
      );
    }

    return "";
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString("sv-SE");
    const formattedTime = date.toLocaleTimeString("sv-SE", { hour12: false });

    return `${formattedDate} - ${formattedTime}`;
  };

  const getAlreadyRegisteredUsers = (
    alreadyRegisteredUsers: ExternalUser[]
  ) => {
    return alreadyRegisteredUsers.length > 0 ? (
      <div>
        <h3>Registrerade Kundanvändare</h3>
        <span>Följande användare är redan tillagda på kunden:</span>
      <div className="overflow-sm-auto">
          <table>
          <thead>
            <tr>
              <th>Namn</th>
              <th>Konto skapat</th>
              <th>Kontot verifierat</th>
              <th>Senaste åtkomst</th>
            </tr>
          </thead>
          <tbody>
            {alreadyRegisteredUsers.map((user) => (
              <tr key={user.email}>
                <td>{user.givenName + " " + user.surName}</td>
                <td>
                  {user.createdAt &&
                    formatDateTime(new Date(user.createdAt).toISOString())}
                </td>
                <td>
                  {user.verified && user.verifiedAt
                    ? formatDateTime(new Date(user.verifiedAt).toISOString())
                    : "Ännu inte verifierad"}
                </td>
                <td>
                  {user.lastAccess && isValidDate(user.lastAccess)
                    ? formatDateTime(new Date(user.lastAccess).toISOString())
                    : "Aldrig loggat in"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    ) : null;
  };

  const getNotifyUsers = (notifyUsers: ExternalUser[]) => {
    return notifyUsers.length > 0 ? (
      <div>
        <span>
          Följande användare är redan aktiverade och kommer att få en notis om
          att de har lagts till på kunden:
        </span>
        <ul>
          {notifyUsers.map((user) => {
            return (
              <li key={user.email}>
                <label>{user.givenName + " " + user.surName}</label>
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;
  };

  const getInviteUsers = (inviteUsers: ExternalUser[]) => {
    const unique = Math.random();
    return inviteUsers.length > 0 ? (
      <div>
        <h3>Kundanvändare</h3>
        <span>Skicka inbjudan per e-post till:</span>
        <ul className="list">
          <li>
            <div className="d-flex align-items-center">
              <input
              type="checkbox"
              id={"alla" + unique}
              onChange={(e) => {
                onInviteAllUsers(e.target.checked);
                setEditingState({
                  isEditing: true,
                  location: "customer-settings"
                });
              }}
              checked={allChecked(inviteUsers)}
            />
            <label htmlFor={"alla" + unique}>
              <strong>Alla kundanvändare</strong>
            </label>
            </div>

            <ul>
              {inviteUsers.map((user) => {
                return (
                  <li key={user.email}>
                    <div className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          onInviteUser(user.email, e.target.checked);
                          setEditingState({
                            isEditing: true,
                            location: "customer-settings"
                          });
                        }}
                        id={user.email + unique}
                        checked={user.sendInvite}
                      />
                      <label htmlFor={user.email + unique}>
                        {user.givenName + " " + user.surName}{" "}
                        {user.invitation
                          ? createInviteText(user.id, user.invitation)
                          : ""}
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </div>
    ) : null;
  };

  const invitationDescription = isWizard ? (
    <span className="addExternalDescription">
      Du kan alltid bjuda in kundanvändare senare genom att gå in på sidan
      Kundinställningar.
    </span>
  ) : null;

  const notifyUsers = externalUsers.filter(
    (u) => u.verified && u.sendNotification
  );

  const separator =
    (externalUsers.length > 0 || notifyUsers.length > 0) &&
    externalUsers.length > 0 ? (
      <hr className="line" />
    ) : null;

  const notifyUsersSection =
    externalUsers.length > 0 ? (
      <div>
        <div>
          {getAlreadyRegisteredUsers(externalUsers)}
          {getNotifyUsers(notifyUsers)}
        </div>
        {separator}
        {getInviteUsers(externalUsers)}
      </div>
    ) : (
      <div>Inga kundanvändare är tillagda på kunden.</div>
    );

  return (
    <SummaryModule
      moduleTitle="Bjud in kundanvändare"
      info="Här ser du vilka av kundanvändare som kommer få ett e-postmeddelande med inbjudan till MittKontor. Om någon av kundanvändarna inte ska få en inbjudan, klickar du bort bocken"
    >
      <>
        {notifyUsersSection}
        {invitationDescription}
      </>
    </SummaryModule>
  );
}
